import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../services/store';

import { PURGE } from 'redux-persist';

import { LibraryState } from '../types/library';
import { selectTrashById } from '../services/trash';
import { SortDirection, SortType } from '../types/common';

const initialState: LibraryState = {
  page: 0,
  options: {
    sortType: SortType.DELETE_DATE,
    sortOrder: SortDirection.DESC,
    layoutType: 'grid',
    isEditMode: false,
    isDetailsOpened: false,
  },
  selectedIds: [],
  selectedItem: null,
};

export const trashSlice = createSlice({
  name: 'trash',
  initialState,
  reducers: {
    trashPageChanged: (state, action) => {
      state.page = action.payload;
    },
    trashSortTypeChanged: (state, action) => {
      state.options.sortType = action.payload;
    },
    trashOrderChanged: (
      state,
      action: PayloadAction<SortDirection, string>
    ) => {
      state.options.sortOrder = action.payload;
    },
    trashLayoutTypeChanged: (state, action) => {
      state.options.layoutType = action.payload;
    },
    trashEditModeToggled: (state, action) => {
      state.options.isEditMode = action.payload;
    },
    trashDetailsToggled: (
      state,
      action: PayloadAction<boolean | undefined, string>
    ) => {
      state.options.isDetailsOpened =
        action.payload ?? !state.options.isDetailsOpened;
    },
    trashItemSelected: (state, action) => {
      state.selectedIds = action.payload.ids;
      if (action.payload.item !== undefined) {
        state.selectedItem = action.payload.item;
      }
    },
    trashIndexListChanged: (state, action) => {
      if (state.selectedItem) {
        state.selectedItem.projectIndex.indexList = action.payload;
      }
    },
    trashIndexIndentChanged: (state, action) => {
      const item = state.selectedItem?.projectIndex.indexList[action.payload];
      if (item) {
        item.indentYn = item.indentYn ? 0 : 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState);
  },
});

export const {
  trashPageChanged,
  trashSortTypeChanged,
  trashOrderChanged,
  trashLayoutTypeChanged,
  trashEditModeToggled,
  trashDetailsToggled,
  trashItemSelected,
  trashIndexListChanged,
  trashIndexIndentChanged,
} = trashSlice.actions;

export const selectTrashPage = (state: RootState) => state.trash.page;

export const selectTrashOptions = (state: RootState) => state.trash.options;

export const selectTrashSelectedIds = (state: RootState) =>
  state.trash.selectedIds;

export const selectTrashSelectedId = createSelector(
  selectTrashSelectedIds,
  (selectedIds) => selectedIds[0]
);

export const selectTrashSelectedItems = (state: RootState) =>
  state.trash.selectedIds.map((id) => selectTrashById(state, id));

export const selectTrashSingleItem = createSelector(
  selectTrashSelectedItems,
  (selectedItems) => selectedItems[0]
);

export const selectTrashSelectedItem = (state: RootState) =>
  state.trash.selectedItem;

export const selectTrash = createSelector(
  selectTrashSelectedItem,
  (selectedItem) => selectedItem?.resource
);

export default trashSlice.reducer;
